/**
 * MB Purchase Options & Free Gifts
 * Handles purchase option and gift selection UI with semantic radio buttons
 */

// ===========================================
// Custom Elements (Shopify pattern)
// ===========================================
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import { initiateAddlFGPromotion } from '@theme/mb-free-gift-promotions';
/**
 * Gift option custom element
 * Provides semantic wrapper for gift radio button selection
 */
class GiftOption extends HTMLElement {
  connectedCallback() {
    const input = this.querySelector('.gift-option-input');
    if (input) {
      input.addEventListener('change', this.handleChange);
      enableDefaultInputs(input);
    }
  }

  disconnectedCallback() {
    const input = this.querySelector('.gift-option-input');
    if (input) {
      input.removeEventListener('change', this.handleChange);
    }
  }

  handleChange = (e) => {
    const purchaseOption = e.target.closest('.purchase-option');
    if(!purchaseOption) return;
 
    // Update form with selected gift variant ID
    const form = purchaseOption.querySelector('.purchase-option-product-form');
    if (!form) return;

    let giftInput = form.querySelector('input[name="properties[_selected_gift_variant_id]"]');
    if (!giftInput) {
      giftInput = document.createElement('input');
      giftInput.type = 'hidden';
      giftInput.name = 'properties[_selected_gift_variant_id]';
      form.appendChild(giftInput);
    }

    giftInput.value = e.target.dataset.giftVariantId || '';
    clearButtonErrors();
  };
}

/**
 * Purchase option custom element
 * Provides semantic wrapper for purchase option radio button selection
 */
class PurchaseOption extends HTMLElement {
  connectedCallback() {
    const input = this.querySelector('.purchase-option-input');
    if (input) {
      input.addEventListener('change', this.handlePurchaseOptionChange);
      enableDefaultInputs(input);
    }
  }

  disconnectedCallback() {
    const input = this.querySelector('.purchase-option-input');
    if (input) {
      input.removeEventListener('change', this.handlePurchaseOptionChange);
    }
  }

  handlePurchaseOptionChange = (e) => {
    toggleGiftOnlyGroups(e.target);

    /* CHANGE THE BUTTON TEXT FOR SUBSCRIPTION */
    updateUniversalButtonText(e.target);
    clearButtonErrors();
  };
}


/**
 * Universal add to cart custom element
 * Custom add to cart button that detects selected purchase options and gifts, and adds them to cart.
 */
class UniversalAddToCart extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', this.handleClick);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.handleClick);
  }

  handleClick = async (e) => {
    e.preventDefault();
    this.clearError(); // clear any existing errors
    try {
      await universal_AddToCart(this);
    } catch (error) {
      this.handleCartError(error);
    }
  }

  /**
   * Handles the cart qty errors for the add to cart button
   * @param - error data
   */
  handleCartError(error) {
    const message = error?.description || error?.message || '';

    if (!message.toLowerCase().includes('maximum limit')) {
      return;
    }

    const productId = this.dataset.productId;

    const addToCartButtons = document.querySelectorAll(
      `universal-add-to-cart[data-product-id="${productId}"]`
    );

    addToCartButtons.forEach(btn => {
      btn.classList.add('disabled');
      btn.showError?.('Your cart contains the maximum quantity of this item.');
    });
  }

  /**
   * Shows error for add to cart error
   */
  showError(message) {
    this.querySelector('.cart-qty-error')?.remove();

    const errorEl = document.createElement('p');
    errorEl.className = 'cart-qty-error';
    errorEl.textContent = message;

    this.insertAdjacentElement('afterend', errorEl);
  }

  /**
   * Clears error state from the button and removes any displayed error messages
   */
  clearError() {
    this.classList.remove('disabled');

    const errorEl = document.querySelectorAll('.cart-qty-error');
    if (errorEl) {
      errorEl.forEach(el => el.remove());
    }
  }
}

if (!customElements.get('gift-option')) {
  customElements.define('gift-option', GiftOption);
}

if (!customElements.get('purchase-option')) {
  customElements.define('purchase-option', PurchaseOption);
}

if (!customElements.get('universal-add-to-cart')) {
  customElements.define('universal-add-to-cart', UniversalAddToCart);
}

/**
 * Clears error states from all universal add to cart buttons
 */
function clearButtonErrors() {
  const universalATCButtons = document.querySelectorAll('universal-add-to-cart');
  if(universalATCButtons.length > 0) {
    universalATCButtons.forEach(btn => btn.clearError?.());
  }
}

/**
 * Auto-selects the first radio input in a group if none is checked
 * @param {HTMLInputElement} input
 */
function enableDefaultInputs(input) {
  const groupName = input.getAttribute('name');
  if (groupName) {
    const anyChecked = document.querySelector(`input[name="${groupName}"]:checked`);
    if (!anyChecked) {
      input.checked = true;
    }
  }
  updateUniversalButtonText(input);
}

/**
 * Updates the universal add to cart button label according to purchase option type
 * @param {HTMLInputElement} input
 */
function updateUniversalButtonText(input) {
  if (!input?.checked) return;

  const type = input.closest('.purchase-option[pdp-single]')?.dataset.type;
  if (!type) return;

  const univButText = type === "addtokit" ? "Add to Subscription" : type === "subscriptionentry" ? "Subscribe" : "Add to Cart";
  document.querySelectorAll('universal-add-to-cart')?.forEach(btn => {
    if (!(input.getAttribute('name') == "purchase_option_quickview" && btn.hasAttribute('data-tc-pdp-sticky-addtocart'))){
      btn.innerHTML = univButText;
    }
  });
}

/**
 * Toggle visibility of gift-only purchase option groups based on selected purchase option
 * Uses progressive search pattern: component scope -> section scope -> document scope
 * @param {HTMLInputElement} selectedRadio - The selected purchase option radio button
 * @returns {void}
 */
function toggleGiftOnlyGroups(selectedRadio) {
  if (!selectedRadio) return;

  const selectedRadioId = selectedRadio.id;
  if (!selectedRadioId) return;

  const purchaseOption = selectedRadio.closest('.purchase-option');
  if(!purchaseOption) return;

  const nestedGiftGroup = purchaseOption.querySelector('.gift-group');
  if(nestedGiftGroup) return; // skip if it has a nested gift group

  // Find all the gift only option group
  const giftOnlyGroups = document.querySelectorAll('.gift-group');

  giftOnlyGroups.forEach((giftGroup) => {
    const activeGiftGroup = giftGroup.querySelector(`div[data-option-ref="${selectedRadioId}"]`);
    const parentDiv = giftGroup.closest('.purchase-option')
    if(activeGiftGroup) {
      parentDiv.style.display = 'block';
    } else {
      parentDiv.style.display = 'none';
    }
  })
}

/**
 * Initialize gift-only group visibility on page load
 * Checks first visible purchase option and ensures first gift in each group is selected
 * @returns {void}
 */
function initGiftOnlyGroups() {
  // Find the first checked purchase option radio button
  let firstChecked = document.querySelector('.purchase-option .purchase-option-input:checked');
  
  if (!firstChecked) {
    // If no option is checked, find and check the first one (with or without header)
    firstChecked = document.querySelector('.purchase-option .purchase-option-input');
    if (firstChecked) {
      firstChecked.checked = true;
    }
  }
  
  if (firstChecked) {
    toggleGiftOnlyGroups(firstChecked);
  }
  
  // Ensure first gift in each visible gift group is checked
  ensureFirstGiftChecked();
}

/**
 * Ensure the first gift in each visible gift group is checked if none are selected
 * Prevents users from submitting without a gift selection
 * @returns {void}
 */
function ensureFirstGiftChecked() {
  // Find all visible purchase options and gift-only groups
  const allPurchaseOptions = document.querySelectorAll('.purchase-option');
  
  allPurchaseOptions.forEach(purchaseOption => {
    // Find all gift groups within this purchase option
    const giftGroups = purchaseOption.querySelectorAll('.gift-group');
    
    giftGroups.forEach(giftGroup => {
      // Check if any gift is already selected in this group
      const checkedGift = giftGroup.querySelector('.gift-option-input:checked');
      if (!checkedGift) {
        // No gift selected, check the first one
        const firstGift = giftGroup.querySelector('.gift-option-input');
        if (firstGift) {
          firstGift.checked = true;
        }
      }
    });
  });
}

// Initialize on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initGiftOnlyGroups);
} else {
  initGiftOnlyGroups();
}

// Reinitialize when sections are dynamically loaded (e.g., via AJAX)
document.addEventListener('shopify:section:load', function(event) {
  initGiftOnlyGroups();
});

/**
 * Generates a random ID string for line item identification
 * @returns {string} - A random unique identifier
 */
function generateRandomId() {
  return crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).slice(2);
}

/**
 * Sets a line item property value in the given form as input hidden field
 * @param {HTMLFormElement} form - The product form element
 * @param {string} key - The property key
 * @param {string} value - The property value
 * @returns {void}
 */
function setLineItemPropertyValue(form, key, value) {
  if(!form) return;

  let input = form.querySelector(`input[name="properties[${key}]"]`);
  
  if (!input) {
    input = document.createElement('input');
    input.type = 'hidden';
    input.name = `properties[${key}]`;
    form.appendChild(input);
  }

  input.value = value;
}

/**
 * Gets the product form and variant ID from the add to cart button
 * @param {Element} addToCartButton - The add to cart button element
 * @returns {Object} - An object containing the form and productId (if found)
 */
function getProductFormAndId(addToCartButton) {
  const form = addToCartButton.closest('form');
  if (!form) return {};

  const idInput = form.querySelector('input[name="id"]');
  if (!idInput) return { form };

  return {
    form,
    productId: idInput.value
  };
}

/**
 * Builds a payload object from a given container
 * Extracts input values and organizes them into a structured object for shopify ajax API calls
 * @param {Element} container - The container element that holds the input fields
 * @returns {object} - An object containing the line item details
 */
export function buildProductDataPayload(container) {
  const inputs = container.querySelectorAll('input, select, textarea');

  const item = {
    id: null,
    quantity: 1,
    properties: {}
  };

  inputs.forEach(input => {
    if (!input.name) return;

    const key = input.name;
    const value = input.value;

    if (key === 'id') {
      item.id = Number(value);
    } else if (key === 'quantity') {
      item.quantity = Number(value);
    } else if (key === 'selling_plan') {
      item.selling_plan = Number(value);
    } else if (key.startsWith('properties[')) {
      const propName = key.match(/properties\[(.*?)\]/)?.[1];
      if (propName) {
        item.properties[propName] = value;
      }
    } else {
      item[key] = value;
    }
  });

  if (Object.keys(item.properties).length === 0) {
    delete item.properties;
  }

  return item;
}

// ===========================================
// Universal Add to Cart (v3 - Elimination of multiple buy button clicks)
// ===========================================

/**
 * Universal add to cart that works for both PDP and SAS pages
 * Finds the selected purchase option and its associated gift(s), and adds to cart in a single click
 * @param {Element} [triggerEl] - Optional trigger element to determine scope
 */
async function universal_AddToCart(triggerEl) {
  const atcButton = triggerEl || document.querySelector('.mb-button');

  const trigger = triggerEl || (window.event && window.event.currentTarget) || document.activeElement;
  if (!trigger) return;

  const scope = trigger.closest('.shopify-section, dialog, .purchase-option-group');
  if (!scope) return;

  const checkedPurchaseOptions = scope.querySelectorAll(
    '.purchase-option .purchase-option-input:checked'
  );

  if(checkedPurchaseOptions.length === 0) {
    console.warn('universal_AddToCart(): no purchase option selected');
    return;
  }

  let purchaseOption = atcButton.previousElementSibling.querySelector('input[name="purchase_option_product"]:checked')?.closest('purchase-option');
  if (purchaseOption == null){
    purchaseOption = atcButton.previousElementSibling.querySelector('input[name="purchase_option_quickview"]:checked')?.closest('purchase-option');
  }
  if (purchaseOption == null && atcButton.hasAttribute('data-tc-pdp-sticky-addtocart')){
    purchaseOption = atcButton.closest('purchase-option');
  }
  //const purchaseOption = document.querySelector('input[name="purchase_option_product"]:checked')?.closest('purchase-option');
  if (purchaseOption && purchaseOption.classList.contains('option-addtokit')) {
    const productId = purchaseOption.querySelector('form').getAttribute('data-product-id');
    const variantId = purchaseOption.querySelector('input[name="id"]').getAttribute('value');
    document.querySelector('#confirm-addtosubscription button[data-confirm-addtosubscription-confirm]')?.addEventListener('click', function(){
      window.handleAddToKit(productId, variantId);
    });
    document.querySelector('#confirm-addtosubscription')?.showDialog();
    return;
  }




    // ADD PRODUCT TO CART
  const itemsToAdd = [];
  const freeGiftInfo = [];
  let mainProductId, mainVariantId = null;
  let isPreupsell = false, preupsellLink = '#', preupsell_product_token = null, freeGiftEligible = false, fromPDP = false;

  checkedPurchaseOptions.forEach((radioInput) => {
    const purchaseOption = radioInput.closest('.purchase-option');
    if(!purchaseOption) return;

    const radioId = radioInput.value;
    const productId = radioInput.dataset.productId;
    const variantId = radioInput.dataset.variantId;
    mainProductId = productId;
    mainVariantId = variantId;
    if(!productId || !variantId) {
      console.warn('universal_AddToCart(): selected purchase option has no product ID');
      return;
    }

    const mainProductForm = purchaseOption.querySelector(`.purchase-option-product-form[data-product-id="${productId}"]`)
    if(!mainProductForm){
      console.warn('universal_AddToCart(): purchase option has no associated product form');
      return;
    };

    const mainItem = buildProductDataPayload(mainProductForm);
    if (!mainItem.id) {
      console.warn('universal_AddToCart(): id not found in main product form');
      return;
    };

    

    // Attach linking property
    const randomId = generateRandomId();
    const lineItemKey = `${mainItem.id}:${randomId}`;
    mainItem.properties = mainItem.properties || {};

    // Prepurchase upsell validation
    const isSubscription = Boolean(mainItem.selling_plan);
    const hasPreupsellLink = Boolean(mainItem.properties._preupsell_link && mainItem.properties._preupsell_link != "");
    const hasPreupsellProduct = Boolean(mainItem.properties._preupsell_product && mainItem.properties._preupsell_product != "");
    const hasPreupsellSellingPlan = Boolean(mainItem.properties._preupsell_product_selling_plan && mainItem.properties._preupsell_product_selling_plan != "");

    if (!isSubscription) {
      // One-time product rule
      if (hasPreupsellLink && hasPreupsellProduct) {
        isPreupsell = true;
        preupsellLink = mainItem.properties._preupsell_link;
        preupsell_product_token = mainItem.id + "::" + mainItem.properties._selling_plan;
      }
    } else {
      // Subscription product rule
      if (hasPreupsellLink && hasPreupsellProduct && hasPreupsellSellingPlan) {
        isPreupsell = true;
        preupsellLink = mainItem.properties._preupsell_link;
        preupsell_product_token = mainItem.id + "::" + mainItem.properties._selling_plan;
      }
    }

    // Check if product added from PDP page(in PDP, cart drawer is opened after ATC operation instead of preupsell page redirection)
    if(mainItem.properties._product_from_PDP && mainItem.properties._product_from_PDP == "PDP") {
      fromPDP = true;
    }

    itemsToAdd.push(mainItem);

    if(itemsToAdd.length === 0) {
      console.warn('universal_AddToCart(): no items to add');
      return;
    }

    // Find associated gifts (nested or unnested)
    let activeGiftOption = null;
    activeGiftOption = purchaseOption.querySelector(
      'gift-option .gift-option-input:checked'
    );

    if(!activeGiftOption && radioId) {
      // if no nested gift found, check for unnested gift-only groups with matchich purchase option id
      const activeGiftOnlyGroup = document.querySelector(`div[data-option-ref="purchase-option-${radioId}"]`);
      if(activeGiftOnlyGroup) {
        activeGiftOption = activeGiftOnlyGroup.querySelector('gift-option .gift-option-input:checked');
      }
    }

    if(!activeGiftOption) {
      console.warn('universal_AddToCart(): no gift option found for selected purchase option');
    }

    if (activeGiftOption) {
      const giftProductId = activeGiftOption.dataset.giftProductId;;
      const giftOption = activeGiftOption.closest('gift-option');
      const isExploded = giftOption.dataset.exploded === 'true';

      if(isExploded) {
        // If gift option is exploded, add all associated variants as separate line items
        const explodedVariantIds = giftOption.dataset.explodedVariantIds ? JSON.parse(giftOption.dataset.explodedVariantIds) : null;
        const explodedGiftInfo = giftOption.dataset.explodedFreeGiftInfo ? JSON.parse(giftOption.dataset.explodedFreeGiftInfo) : null;

        if (!explodedVariantIds.length || explodedVariantIds.length === 0 ) {
          console.warn('universal_AddToCart(): exploded gift option has no variant items');
          return;
        }

        //add free gift info data
        if(explodedGiftInfo && explodedGiftInfo.length > 0 ) {
          freeGiftInfo.push(explodedGiftInfo);
        }

        explodedVariantIds.forEach((id) => {
          freeGiftEligible = true;

          itemsToAdd.push({
            id: Number(id),
            quantity: 1,
            properties: {
              _qualifying_line_item_id: lineItemKey,
              _free_gift: 'true',
            },
          });
        });
      } else {
        const giftProductForm = giftOption.querySelector(`.gift-option-product-form[data-product-id="${giftProductId}"]`)
        if(!giftProductForm) {
          console.warn('universal_AddToCart(): gift option has no associated product form');
          return
        };

        const giftItem = buildProductDataPayload(giftProductForm);
        if (!giftItem.id) {
          console.warn('universal_AddToCart(): id not found in gift product form');
          return;
        };

        //add free gift info data
        freeGiftInfo.push(`${giftItem.id}:${giftItem.title}`);

        // If gift has a variant picker, ensure the selected variant is added to the cart instead of default gift variant
        const variantPicker = giftOption.querySelector('variant-picker');

        if (variantPicker) {
          const selectedVariant = variantPicker.querySelector('fieldset input:checked, select option:checked');
          const variantId = selectedVariant?.dataset?.variantId;

          if (variantId) {
            giftItem.id = Number(variantId);
          } else {
            console.warn('universal_AddToCart(): Gift has variant picker but no variant selected');
          }
        }
        
        if (giftItem.id) {
          freeGiftEligible = true;
          giftItem.properties = giftItem.properties || {};
          giftItem.properties._qualifying_line_item_id = lineItemKey;
          giftItem.properties._free_gift = "true";

          itemsToAdd.push(giftItem);
        }
      }
    }

    // Attach separate lineitem id to main product
    if(hasPreupsellLink || freeGiftEligible) {
      mainItem.properties._line_item_id = lineItemKey;
      // if free gift eligible, attach free gift info
      if(freeGiftEligible) {
        mainItem.properties._free_gift_info = JSON.stringify(freeGiftInfo);
        freeGiftEligible = false;
      } 
    }
  });

  let attributes = { _preupsell_product_token: preupsell_product_token};

  if (!itemsToAdd.length) return;

  const cartItemsComponents = document.querySelectorAll('cart-items-component');
  let cartItemComponentsSectionIds = [];
  cartItemsComponents.forEach((item) => {
    if (item instanceof HTMLElement && item.dataset.sectionId) {
      cartItemComponentsSectionIds.push(item.dataset.sectionId);
    }
  });

  try {
    // Disable the add to cart button to prevent multiple clicks
    atcButton?.classList.add('loading');
    const response = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: itemsToAdd, sections: cartItemComponentsSectionIds, attributes: attributes })
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw errorData;
    }

    const data = await response.json();

    await morphSection(cartItemComponentsSectionIds[0], data.sections[cartItemComponentsSectionIds[0]]);
    await initiateAddlFGPromotion();
    if(isPreupsell && !fromPDP) {
      window.location.href = preupsellLink;
    } else {
      document.querySelector('cart-drawer-component').open();
    }
  } catch (error) {
    console.error('universal_AddToCart(): Cart error', error);
    throw error;
  } finally {
    // resetting atc button state
    atcButton?.classList.remove('loading');
  }
  // end
}

/**
 * Universal click handler for buy buttons
 * Automatically detects and adds selected gifts when a buy button is clicked
 * Works for both direct clicks (PDP) and programmatic clicks (SAS via universal_AddToCart)
 */
function initUniversalGiftHandler() {
  // Define scope selectors in order of specificity (most specific first)
  // Easily extensible - just add new selectors to this array
  const SCOPE_SELECTORS = [
    'product-card',           // Product cards in grids/recommendations
    '.shopify-section',       // Shopify sections (PDP, SAS)
    'dialog',                 // Modals/dialogs
    '.purchase-option-group', // Purchase option groups
    'body'                    // Page-level fallback
  ];

  let processingClick = false;

  document.addEventListener('click', async function(e) {
    // Avoid recursive processing
    if (processingClick) return;

    const btn = e.target?.closest('button, input');
    if (!btn) return;

    // Check if this is a buy button (not a gift button)
    const addToCartComponent = btn.closest('add-to-cart-component');
    if (!addToCartComponent) return;
    
    // Skip if this is inside a gift-option (we only want the main product buy button)
    if (btn.closest('gift-option')) return;

    // Find the closest scope container by checking selectors in order
    let scope = null;
    for (const selector of SCOPE_SELECTORS) {
      scope = btn.closest(selector);
      if (scope) break;
    }

    if (!scope) return;

    // Look for checked purchase option radio buttons in the found scope
    const checkedPurchaseOptions = scope.querySelectorAll('.purchase-option .purchase-option-input:checked');
    if (checkedPurchaseOptions.length === 0) return;

    // Collect all selected gifts from checked purchase options
    const giftsToAdd = [];
    checkedPurchaseOptions.forEach((radioInput) => {
      const purchaseOption = radioInput.closest('.purchase-option');
      if (!purchaseOption) return;

      // Look for nested gifts within this purchase option
      const nestedGiftInput = purchaseOption.querySelector('gift-option .gift-option-input:checked');
      if (nestedGiftInput) {
        const giftOption = nestedGiftInput.closest('gift-option');
        const giftBuyButton = giftOption?.querySelector('add-to-cart-component [ref="addToCartButton"]');
        if (giftBuyButton) {
          giftsToAdd.push(giftBuyButton);
        } else {
          console.warn('universal gift handler: selected nested gift has no buy button', giftOption);
        }
      }

      // Look for unnested gift-only groups with matching radio ID
      const radioId = radioInput.id;
      if (giftsToAdd.length === 0 && radioId) {
        const activeGiftOnlyGroup = document.querySelector(`div[data-option-ref="${radioId}"]`);
        if(activeGiftOnlyGroup) {
          const selectedGiftInput = activeGiftOnlyGroup.querySelector('gift-option .gift-option-input:checked');
          if (selectedGiftInput) {
            const giftOption = selectedGiftInput.closest('gift-option');
            const giftBuyButton = giftOption?.querySelector('add-to-cart-component [ref="addToCartButton"]');
            if (giftBuyButton) {
              giftsToAdd.push(giftBuyButton);
            } else {
              console.warn('universal gift handler: selected gift has no buy button', giftOption);
            }
          }
        } else {
          // Find the parent container that holds sibling groups
          const parentContainer = purchaseOption.closest('.purchase-option-group')?.parentElement;
          if (parentContainer) {
            // Find all gift-only groups (those with hidden radios matching this ID)
            const giftOnlyGroups = parentContainer.querySelectorAll('.purchase-option-group');
            giftOnlyGroups.forEach(group => {
              const groupRadio = group.querySelector('.purchase-option-input');
              if (!groupRadio) return;

              // Check if this is a gift-only group with matching ID
              const isGiftOnlyGroup = groupRadio.style.display === 'none' || 
                                      !group.querySelector('.purchase-option-header');
              
              if (isGiftOnlyGroup && groupRadio.id === radioId) {
                // Find the selected gift in this gift-only group
                const giftInput = group.querySelector('gift-option .gift-option-input:checked');
                if (giftInput) {
                  const giftOption = giftInput.closest('gift-option');
                  const giftBuyButton = giftOption?.querySelector('add-to-cart-component [ref="addToCartButton"]');
                  if (giftBuyButton) {
                    giftsToAdd.push(giftBuyButton);
                  } else {
                    console.warn('universal gift handler: selected unnested gift has no buy button', giftOption);
                  }
                }
              }
            });
          }
        }
        
      }
    });

    if (giftsToAdd.length === 0) return; // No gifts selected, allow normal add to cart

    // Allow the main product to add to cart normally (don't prevent default)
    // Then add the gift(s) after a short delay to ensure the product was added first
    processingClick = true;
   
    setTimeout(() => {
      giftsToAdd.forEach(giftBtn => {
        giftBtn.click();
      });
      processingClick = false;
    }, 500); // 500ms delay to allow main product to process
    
  }, true); // Use capture phase to catch clicks early
}

// Initialize the universal handler on DOMContentLoaded
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initUniversalGiftHandler);
} else {
  initUniversalGiftHandler();
}

// Export for inline event handlers
window.universal_AddToCart = universal_AddToCart;

/**
 * Variant change listener to update purchase option forms with the selected variant ID
 * Listens for 'variant:selected' events and updates the hidden variant ID inputs in purchase options accordingly
 * Ensures that the correct variant is added to cart when a purchase option is selected after changing variants
 * Ignores variant changes triggered from within purchase options to prevent interference with gift variant selection
 */
document.addEventListener('variant:selected', (event) => {
  clearButtonErrors();
  if(!event.target) return;

  // Ignore variant pickers inside purchase options
  if (event.target.closest('.purchase-option')) {
    return;
  }

  const selectedVariant = event.target.querySelector('fieldset input:checked, select option:checked');
  const selectedVariantId = selectedVariant?.dataset?.variantId;
  
  const purchaseOptions = document.querySelectorAll('.purchase-option');
  if(purchaseOptions.length === 0) {
    console.warn('variant:selected event: no purchase options found');
    return;
  }

  // Update all purchase options with the selected variant ID
  purchaseOptions.forEach(option => {
    const selectedInput = option.querySelector('.purchase-option-input');
    if(!selectedInput) return;

    // Update the purchase option input with the new variant ID
    selectedInput.setAttribute('data-variant-id', selectedVariantId);
    selectedInput.value = selectedVariantId;

    // Update the hidden variant ID input in the associated purchase option form
    const variantInput = option.querySelector('.purchase-option-product-form input[name="id"]');
    if (variantInput) {
      variantInput.value = selectedVariantId;
    }
  });
});

//STOP THE PDP STICKY FOOTER FROM SCROLLING TO THE TOP FOR APPARENTLY NO REASON.
//document.querySelector('.purchase-option .sticky-footer')?.addEventListener('click', function(e){ e.preventDefault(); });
document.querySelectorAll('.purchase-option .sticky-footer').forEach(element => {
  element.addEventListener('click', function(e) { 
    e.preventDefault(); 
  });
});